/**
 * Toast notification system.
 * - ToastProvider wraps the app to provide context
 * - Toast renders the actual notification
 * - useToast() hook gives { showToast(message, type) }
 */

import { createContext } from 'preact';
import { useContext, useCallback, useRef } from 'preact/hooks';
import { signal } from '@preact/signals';

// --- Types ---

type ToastType = 'success' | 'error' | 'info';

interface ToastState {
  message: string;
  type: ToastType;
  visible: boolean;
}

interface ToastContextValue {
  showToast: (message: string, type?: ToastType) => void;
}

// --- Signals ---

const toastState = signal<ToastState>({
  message: '',
  type: 'info',
  visible: false,
});

// --- Context ---

const ToastContext = createContext<ToastContextValue>({
  showToast: () => {},
});

// --- Provider ---

export function ToastProvider({ children }: { children: preact.ComponentChildren }) {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const showToast = useCallback((message: string, type: ToastType = 'info') => {
    // Clear any existing timer
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    toastState.value = { message, type, visible: true };

    timerRef.current = setTimeout(() => {
      toastState.value = { ...toastState.value, visible: false };
    }, 3000);
  }, []);

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
    </ToastContext.Provider>
  );
}

// --- Hook ---

export function useToast(): ToastContextValue {
  return useContext(ToastContext);
}

// --- Render component ---

const typeColors: Record<ToastType, { background: string; color: string; border: string }> = {
  success: { background: '#166534', color: '#86efac', border: '#22c55e' },
  error: { background: '#7f1d1d', color: '#fca5a5', border: '#ef4444' },
  info: { background: 'var(--bg-tertiary)', color: 'var(--text-primary)', border: 'var(--border)' },
};

export function Toast() {
  const state = toastState.value;

  const colors = typeColors[state.type];

  return (
    <div
      style={{
        position: 'fixed',
        bottom: 'calc(80px + var(--safe-bottom))',
        left: '50%',
        transform: `translateX(-50%) translateY(${state.visible ? '0' : '20px'})`,
        background: colors.background,
        color: colors.color,
        padding: '10px 24px',
        borderRadius: '20px',
        fontSize: '14px',
        fontWeight: '500',
        opacity: state.visible ? 1 : 0,
        transition: 'all 0.3s ease',
        zIndex: 1000,
        pointerEvents: 'none',
        border: `1px solid ${colors.border}`,
        maxWidth: 'calc(100vw - 48px)',
        textAlign: 'center',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}
      role="status"
      aria-live="polite"
    >
      {state.message}
    </div>
  );
}
